import { ArrowLeftIcon } from '@components/Icons/ArrowLeftIcon';
import { formatBytesToString, formatDurationToString, pluralize } from '@lib/utils/units';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Skeleton } from '@mui/material';
import { useClaimMobileNavCorner } from '@pages/App/components/Nav/mobileNavCorner';
import { MobileDeleteLibraryDrawer } from '@pages/Libraries/components/Mobile/MobileDeleteLibraryDrawer';
import { MobileEditLibraryModal } from '@pages/Libraries/components/Mobile/MobileEditLibraryModal';
import { MobileLibraryContextDrawer } from '@pages/Libraries/components/Mobile/MobileLibraryContextDrawer';
import { TranscriptOnlyIndicator } from '@pages/Libraries/components/TranscriptOnlyIndicator';
import { matchesOmniFilter, parseOmniQuery } from '@pages/Library/utils/omniFilter';
import { getUploadHeaderLabel } from '@pages/Library/utils/uploadCalculations';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import css from './MobileLibraryHeader.module.scss';

export const MobileLibraryHeader = observer(function MobileLibraryHeader() {
  const library = useLibrary();
  const folder = useLibraryFolder();
  const uploads = useUploadManager();
  const loaded = library.name !== undefined;
  // "All libraries" takes the corner, so the page has one left edge rather than an 80px indent
  // around a floating menu button. The menu is a step back, on the Libraries page.
  useClaimMobileNavCorner();

  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const disableDelete = library.isDefault || library.isDemo || library.isReadOnly;
  const isReadOnly = library.isReadOnly;

  // Stats
  const currentLibraryId = folder.library?.libraryId ?? null;
  const parsedOmni = parseOmniQuery(folder._omniFilter, omniFilterPreferences.matchAll);
  const filteredUploadCount = Object.values(uploads.uploadStatus).filter((s) => {
    if (s.status !== 'queued' && s.status !== 'uploading' && s.status !== 'success') return false;
    if (!folder.holdsUpload(s.destination)) return false;
    if (s.media?._id && folder.files.some((f) => f._id === s.media?._id && f.status !== 'empty'))
      return false;
    if (parsedOmni && !matchesOmniFilter({ name: s.file.name, mediaId: s.media?._id }, parsedOmni))
      return false;
    return true;
  }).length;
  const mediaCount = folder.filesCountDisplay + filteredUploadCount;
  const uploadingCount =
    currentLibraryId == null
      ? 0
      : Object.values(uploads.uploadStatus).filter(
          (s) =>
            (s.status === 'queued' || s.status === 'uploading') &&
            s.destination.libraryId === currentLibraryId,
        ).length;
  const backendProcessingCount = folder.filesIndexing.length + folder.filesQueued.length;

  const duration =
    folder._omniFilter || !folder.showsWholeLibrary
      ? folder.filesFilteredDuration
      : library.stats?.mediaDuration;
  const formattedDuration = duration
    ? formatDurationToString(duration, { spaceBeforeUnit: false })
    : null;

  const sizeInBytes =
    folder._omniFilter || !folder.showsWholeLibrary
      ? folder.filesFilteredSize
      : library.stats?.mediaSize;
  const formattedSize = sizeInBytes
    ? formatBytesToString(sizeInBytes * 1000 * 1000, { spaceBeforeUnit: false })
    : null;

  return (
    <div className={css.header}>
      <div className={css.topRow}>
        <div className={css.nameGroup}>
          <div className={css.nameAndInfo}>
            {/* The desktop page header carries "Back to all libraries", and it isn't shown on a
                phone. */}
            <Link to="/libraries" className={css.back}>
              <ArrowLeftIcon />
              All libraries
            </Link>
            <div className={css.titleRow}>
              {loaded ? (
                <h2 className={css.name}>{library.name}</h2>
              ) : (
                <Skeleton variant="text" height="28px" width="16ch" />
              )}
              {loaded && library.isTranscriptOnly && (
                <TranscriptOnlyIndicator className={css.nameTranscriptIcon} />
              )}
              {!isReadOnly && loaded && (
                <IconButton
                  className={css.menuButton}
                  onClick={() => setContextDrawerOpen(true)}
                  aria-label="library menu"
                  aria-haspopup="true"
                  aria-expanded={contextDrawerOpen}
                >
                  <MoreHorizIcon />
                </IconButton>
              )}
            </div>
            {loaded ? (
              <>
                <span className={css.infoText}>
                  {pluralize(mediaCount, 'media file')}
                  {formattedDuration && <> &bull; {formattedDuration}</>}
                  {formattedSize && <> &bull; {formattedSize}</>}
                </span>
                {uploadingCount > 0 && (
                  <span className={css.processingLine}>
                    {getUploadHeaderLabel(uploads.globalUploadEta, uploadingCount)}
                  </span>
                )}
                {uploadingCount === 0 && backendProcessingCount > 0 && (
                  <span className={css.processingLine}>
                    Processing {backendProcessingCount} media, you may close this tab.
                  </span>
                )}
              </>
            ) : (
              <Skeleton variant="text" height="16px" width="20ch" />
            )}
          </div>
        </div>
      </div>

      {/* Context menu + modals. On mobile the ⋯ opens the shared drawer (same as the
          libraries page); rename opens the mobile-native edit sheet. */}
      {!isReadOnly && (
        <>
          <MobileLibraryContextDrawer
            surface="library"
            open={contextDrawerOpen}
            onClose={() => setContextDrawerOpen(false)}
            onOpen={() => setContextDrawerOpen(true)}
            libraryId={library.libraryId}
            disableDelete={disableDelete}
            onRename={() => setEditOpen(true)}
            onDelete={() => setDeleteDrawerOpen(true)}
          />
          <MobileEditLibraryModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            libraryId={library.libraryId}
            currentName={library.name || ''}
          />
          <MobileDeleteLibraryDrawer
            open={deleteDrawerOpen}
            onClose={() => setDeleteDrawerOpen(false)}
            onOpen={() => setDeleteDrawerOpen(true)}
            libraryId={library.libraryId}
            currentName={library.name || ''}
            onDeleted={() => {
              setDeleteDrawerOpen(false);
              setContextDrawerOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
});
